import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Table,
    Card,
    Input,
    Button,
    Select,
    Tag,
    Row,
    Col,
    Spin,
    Alert,
    Space,
    Typography,
    Statistic,
    DatePicker,
    Tooltip,
    Popconfirm,
    message
} from 'antd';
import { SearchOutlined, ClearOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { RootState } from '../../app/store';
import { REACT_APP_API_URL } from '../../configs/config';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface UserInfo {
    email: string;
    username: string;
    fullName: string;
    createdAt: string;
}

interface AdViewHistory {
    id: string;
    userId: string;
    userInfo: UserInfo | null;
    adProvider: 'admob' | 'unity' | 'vungle';
    adType: 'rewarded' | 'interstitial';
    adId: string | null;
    rewardCoin: number;
    watchedAt: string;
    isSynced: boolean;
    syncedAt: string | null;
}

interface ProviderStat {
    _id: string;
    count: number;
    totalCoins: number;
}

interface TypeStat {
    _id: string;
    count: number;
    totalCoins: number;
}

interface Summary {
    totalRewardCoins: number;
    providerStats: ProviderStat[];
    typeStats: TypeStat[];
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface ApiResponse {
    success: boolean;
    data: {
        histories: AdViewHistory[];
        pagination: Pagination;
        summary: Summary;
    };
}

const AdsHistoryPage: React.FC = () => {
    const token = useSelector((state: RootState) => state.auth.token);
    const [histories, setHistories] = useState<AdViewHistory[]>([]);
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [summary, setSummary] = useState<Summary>({
        totalRewardCoins: 0,
        providerStats: [],
        typeStats: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filter states
    const [filters, setFilters] = useState({
        userId: '',
        adProvider: '',
        adType: '',
        dateRange: null as [Dayjs, Dayjs] | null
    });

    const fetchAdHistory = async (page: number = 1) => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: pagination.limit.toString()
            });

            if (filters.userId) params.append('userId', filters.userId);
            if (filters.adProvider) params.append('adProvider', filters.adProvider);
            if (filters.adType) params.append('adType', filters.adType);
            if (filters.dateRange) {
                params.append('startDate', filters.dateRange[0].format('YYYY-MM-DD'));
                params.append('endDate', filters.dateRange[1].format('YYYY-MM-DD'));
            }


            const response = await fetch(`${REACT_APP_API_URL}api/ad-sync/history?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Pragma': 'no-cache',
                    'Cache-Control': 'no-cache',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: ApiResponse = await response.json();

            if (data.success) {
                setHistories(data.data.histories);
                setPagination(data.data.pagination);
                setSummary(data.data.summary);
            } else {
                throw new Error('API returned unsuccessful response');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdHistory(1);
        // Search and pagination handlers control subsequent loads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (field: string, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        fetchAdHistory(1);
    };

    const handleClearFilters = () => {
        setFilters({
            userId: '',
            adProvider: '',
            adType: '',
            dateRange: null
        });
    };

    const handlePageChange = (page: number) => {
        fetchAdHistory(page);
    };

    const deleteAdHistoryById = async (id: string) => {
        try {
            setLoading(true);
            const response = await fetch(`${REACT_APP_API_URL}api/ad-sync/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                message.error(`Failed to delete: ${response.status} - ${errorText}`);
                return;
            }

            const resJson = await response.json();
            const deletedId = resJson?.data?.deletedId || id;
            const deductedCoins = resJson?.data?.deductedCoins;
            const userId = resJson?.data?.userId;

            message.success(
                deductedCoins != null
                    ? `Deleted ${deletedId}. Deducted ${deductedCoins} coins for user ${userId}.`
                    : `Deleted ${deletedId} successfully.`
            );

            fetchAdHistory(pagination.page);
        } catch (err: any) {
            message.error(err?.message || 'An error occurred while deleting');
        } finally {
            setLoading(false);
        }
    };

    const getProviderColor = (provider: string) => {
        switch (provider) {
            case 'admob': return 'blue';
            case 'unity': return 'purple';
            case 'vungle': return 'green';
            default: return 'default';
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'rewarded': return 'green';
            case 'interstitial': return 'orange';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'User Info',
            dataIndex: 'userInfo',
            key: 'userInfo',
            render: (userInfo: UserInfo | null, record: AdViewHistory) => (
                <div>
                    {userInfo ? (
                        <>
                            <Text strong>{userInfo.fullName || userInfo.username}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {userInfo.email}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                ID: {record.userId}
                            </Text>
                        </>
                    ) : (
                        <Text type="secondary">User ID: {record.userId}</Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Ad Provider',
            dataIndex: 'adProvider',
            key: 'adProvider',
            render: (provider: string) => (
                <Tag color={getProviderColor(provider)}>
                    {provider.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Ad Type',
            dataIndex: 'adType',
            key: 'adType',
            render: (type: string) => (
                <Tag color={getTypeColor(type)}>
                    {type.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Reward Coins',
            dataIndex: 'rewardCoin',
            key: 'rewardCoin',
            render: (coins: number) => (
                <Text strong style={{ color: '#52c41a' }}>
                    +{coins}
                </Text>
            ),
        },
        {
            title: 'Watched At',
            dataIndex: 'watchedAt',
            key: 'watchedAt',
            render: (date: string) => dayjs(date).format('MMM DD, YYYY HH:mm'),
        },
        {
            title: 'Sync Status',
            dataIndex: 'isSynced',
            key: 'isSynced',
            render: (isSynced: boolean, record: AdViewHistory) => (
                <div>
                    <Tag color={isSynced ? 'success' : 'warning'}>
                        {isSynced ? 'Synced' : 'Not Synced'}
                    </Tag>
                    {record.syncedAt && (
                        <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {dayjs(record.syncedAt).format('MMM DD, HH:mm')}
                            </Text>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_: any, record: AdViewHistory) => (
                <Popconfirm
                    title="Delete this record?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                    onConfirm={() => deleteAdHistoryById(record.id)}
                >
                    <Tooltip title="Delete">
                        <Button icon={<DeleteOutlined />} danger size="small" />
                    </Tooltip>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Ad View History Management</Title>

            {/* Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} md={8}>
                    <Card>
                        <Statistic
                            title="Total Reward Coins"
                            value={summary.totalRewardCoins}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card>
                        <Statistic
                            title="Total Records"
                            value={pagination.total}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card>
                        <div>
                            <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                                Ad Providers
                            </Text>
                            <div style={{ marginTop: '8px' }}>
                                <Space wrap>
                                    {summary.providerStats.map((stat) => (
                                        <Tag key={stat._id} color={getProviderColor(stat._id)}>
                                            {stat._id}: {stat.count}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card style={{ marginBottom: '24px' }}>
                <Title level={4}>Filters</Title>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={4}>
                        <Input
                            placeholder="User ID"
                            value={filters.userId}
                            onChange={(e) => handleFilterChange('userId', e.target.value)}
                        />
                    </Col>
                    <Col xs={24} md={4}>
                        <Select
                            placeholder="Ad Provider"
                            value={filters.adProvider || undefined}
                            onChange={(value) => handleFilterChange('adProvider', value || '')}
                            style={{ width: '100%' }}
                            allowClear
                        >
                            <Option value="admob">AdMob</Option>
                            <Option value="unity">Unity</Option>
                            <Option value="vungle">Vungle</Option>
                        </Select>
                    </Col>
                    <Col xs={24} md={4}>
                        <Select
                            placeholder="Ad Type"
                            value={filters.adType || undefined}
                            onChange={(value) => handleFilterChange('adType', value || '')}
                            style={{ width: '100%' }}
                            allowClear
                        >
                            <Option value="rewarded">Rewarded</Option>
                            <Option value="interstitial">Interstitial</Option>
                        </Select>
                    </Col>
                    <Col xs={24} md={6}>
                        <RangePicker
                            value={filters.dateRange}
                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                loading={loading}
                            >
                                Search
                            </Button>
                            <Button
                                icon={<ClearOutlined />}
                                onClick={handleClearFilters}
                            >
                                Clear
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: '24px' }}
                />
            )}

            {/* Data Table */}
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Title level={4} style={{ margin: 0 }}>
                        Ad View History ({pagination.total} records)
                    </Title>
                    {loading && <Spin />}
                </div>

                <Table
                    columns={columns}
                    dataSource={histories}
                    rowKey="id"
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        onChange: handlePageChange,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    loading={loading}
                    scroll={{ x: 800 }}
                />
            </Card>

            {/* Provider and Type Statistics */}
            <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
                <Col xs={24} md={12}>
                    <Card>
                        <Title level={4}>Provider Statistics</Title>
                        {summary.providerStats.map((stat) => (
                            <div key={stat._id} style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Tag color={getProviderColor(stat._id)}>
                                        {stat._id.toUpperCase()}
                                    </Tag>
                                    <Text>
                                        {stat.count} views • {stat.totalCoins} coins
                                    </Text>
                                </div>
                            </div>
                        ))}
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card>
                        <Title level={4}>Type Statistics</Title>
                        {summary.typeStats.map((stat) => (
                            <div key={stat._id} style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Tag color={getTypeColor(stat._id)}>
                                        {stat._id.toUpperCase()}
                                    </Tag>
                                    <Text>
                                        {stat.count} views • {stat.totalCoins} coins
                                    </Text>
                                </div>
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AdsHistoryPage;
